import type { AxiosError } from 'axios';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { surveysService } from '@/services/surveys.service';
import { useAuthStore } from '@/stores/auth';
import { toast } from 'sonner';

export const surveyKeys = {
  all: ['surveys'] as const,
  list: (filters?: Parameters<typeof surveysService.findAll>[0]) => [...surveyKeys.all, 'list', filters] as const,
  detail: (id: string) => [...surveyKeys.all, 'detail', id] as const,
  responses: (id: string) => [...surveyKeys.all, 'responses', id] as const,
  stats: (id: string) => [...surveyKeys.all, 'stats', id] as const,
  myPending: () => [...surveyKeys.all, 'my-pending'] as const,
  myResponses: () => [...surveyKeys.all, 'my-responses'] as const,
};

// Admin
export function useSurveys(params?: {
  page?: number;
  status?: string;
  productId?: string;
}) {
  return useQuery({
    queryKey: surveyKeys.list(params),
    queryFn: () => surveysService.findAll(params),
  });
}

export function useSurvey(id: string) {
  return useQuery({
    queryKey: surveyKeys.detail(id),
    queryFn: () => surveysService.findById(id),
    enabled: !!id,
  });
}

export function useCreateSurvey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: surveysService.create,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: surveyKeys.all });
      toast.success('Pesquisa criada!');
    },
    onError: () => toast.error('Erro ao criar pesquisa'),
  });
}

export function useUpdateSurvey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...dto }: { id: string } & Parameters<typeof surveysService.update>[1]) =>
      surveysService.update(id, dto),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: surveyKeys.all });
      toast.success('Pesquisa atualizada!');
    },
    onError: () => toast.error('Erro ao atualizar pesquisa'),
  });
}

export function useDeleteSurvey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: surveysService.delete,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: surveyKeys.all });
      toast.success('Pesquisa excluída');
    },
    onError: () => toast.error('Erro ao excluir'),
  });
}

export function useDuplicateSurvey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: surveysService.duplicate,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: surveyKeys.all });
      toast.success('Pesquisa duplicada!');
    },
    onError: () => toast.error('Erro ao duplicar'),
  });
}

export function useSurveyResponses(surveyId: string, params?: Parameters<typeof surveysService.getResponses>[1]) {
  return useQuery({
    queryKey: surveyKeys.responses(surveyId),
    queryFn: () => surveysService.getResponses(surveyId, params),
    enabled: !!surveyId,
  });
}

export function useSurveyStats(surveyId: string) {
  return useQuery({
    queryKey: surveyKeys.stats(surveyId),
    queryFn: () => surveysService.getStats(surveyId),
    enabled: !!surveyId,
  });
}

// User
export function useMyPendingSurveys(productId?: string) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const accessToken = useAuthStore((s) => s.accessToken);
  const user = useAuthStore((s) => s.user);
  // Only run after user has accepted terms — avoids 401 during onboarding/terms flow
  const termsAccepted = !!(user as { termsAcceptedAt?: string | null })
    ?.termsAcceptedAt;
  return useQuery({
    queryKey: [...surveyKeys.myPending(), user?.id, productId],
    queryFn: () => surveysService.getMyPending(productId),
    enabled: isAuthenticated && !!accessToken && termsAccepted,
  });
}

export function useMyResponses() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const accessToken = useAuthStore((s) => s.accessToken);
  return useQuery({
    queryKey: surveyKeys.myResponses(),
    queryFn: surveysService.getMyResponses,
    enabled: isAuthenticated && !!accessToken,
  });
}

export function useRespondSurvey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      surveyId,
      answers,
      triggerType,
      triggerContext,
    }: {
      surveyId: string;
      answers: Array<{ questionId: string; value: string }>;
      triggerType?: string;
      triggerContext?: { productId: string };
    }) => surveysService.respond(surveyId, answers, triggerType, triggerContext),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: surveyKeys.myPending() });
      qc.invalidateQueries({ queryKey: surveyKeys.myResponses() });
      toast.success('Resposta enviada com sucesso!');
    },
    onError: (err: AxiosError<{ message?: string }>) =>
      toast.error(err.response?.data?.message || 'Erro ao enviar resposta'),
  });
}
